import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { ESymbol } from '../../pdf/draw/symbol';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';

/**
 * Kostky (Dice) - Foldable cube nets with labels
 *
 * Layout: 1 landscape page with 2 cube nets (5 faces each + 1 "?" face).
 * Each net has fold flaps for assembly.
 *
 * Migrated from C# EduFrame.Img10.Generators.Kostky
 */
export class Kostky implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 12;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const page = pdf.addPage(true); // landscape

    const margin = PdfUnit.FromMillimeters(5);
    const flap = PdfUnit.FromMillimeters(10);
    const size = (PdfSize.A4.width - 2 * margin - flap) / 4;

    const count = opts.list.length;
    const allTexts = opts.list.map((item) => item.left.getText() ?? '');
    const font = page.adjustFontMulti(
      allTexts.map((t) => [t]),
      GUtil.baseFont,
      new PdfSize(size, size)
    );

    const draw = (faceIndex: number, bx: number, by: number) => {
      const rect = new PdfRect(margin + bx * size, margin + flap + by * size, size, size);

      if (faceIndex < count) {
        page.drawLabel(opts.list[faceIndex].left, { rect, font });
      } else {
        page.drawSymbol(ESymbol.FullStar, PdfBrush.Black, rect, 48, ETextAlign.Center);
      }

      page.drawRectangle(GUtil.basePen, null, rect);
    };

    const drawFlap = (bx: number, by: number, sx: number, sy: number, px: number, py: number) => {
      const x = margin + bx * size;
      const y = margin + flap + by * size;

      const p1 = new PdfPoint(x, y);
      const p2 = new PdfPoint(x + flap * sx, y + flap * sy);
      const p3 = new PdfPoint(p2.x + px * (size - 2 * flap), p2.y + py * (size - 2 * flap));
      const p4 = new PdfPoint(x + size * px, y + size * py);

      page.drawLines(GUtil.basePen, [p1, p2, p3, p4]);
    };

    // First cube (even indices: 0, 2, 4, 6, 8, 10)
    draw(0, 1, 0);
    draw(2, 0, 1);
    draw(4, 1, 1);
    draw(6, 2, 1);
    draw(8, 1, 2);
    draw(10, 1, 3);
    drawFlap(1, 0, 1, -1, 1, 0);
    drawFlap(1, 0, -1, 1, 0, 1);
    drawFlap(2, 0, 1, 1, 0, 1);
    drawFlap(1, 2, -1, 1, 0, 1);
    drawFlap(2, 2, 1, 1, 0, 1);
    drawFlap(1, 3, -1, 1, 0, 1);
    drawFlap(2, 3, 1, 1, 0, 1);

    // Second cube (odd indices: 1, 3, 5, 7, 9, 11)
    draw(1, 4, 0);
    draw(3, 4, 1);
    draw(5, 3, 2);
    draw(7, 4, 2);
    draw(9, 5, 2);
    draw(11, 4, 3);
    drawFlap(4, 0, 1, -1, 1, 0);
    drawFlap(4, 0, -1, 1, 0, 1);
    drawFlap(5, 0, 1, 1, 0, 1);
    drawFlap(4, 1, -1, 1, 0, 1);
    drawFlap(5, 1, 1, 1, 0, 1);
    drawFlap(4, 3, -1, 1, 0, 1);
    drawFlap(5, 3, 1, 1, 0, 1);
  }
}
